// src/screens/BlastResultsScreen/BlastResultsScreen.jsx
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../../context/GameContext';
import { useAuth } from '../../context/AuthContext';
import { fetchWithRetry } from '../../utils/api';
import { QuizFooter, QuizFooterRow } from '../../components/common/QuizFooter';
import '../ResultsScreen/results-screen.css';
import './blast-results-screen.css';

export const formatTitle = (position, betType, config = {}) => {
  if (!position || !betType) return 'Range';

  if (betType.includes('SB_LIMP') || betType === 'LIMP' ||
      betType.includes('LIMP') || betType === 'SB_LIMP') {
    return `${position} Limp Range`;
  }

  if (betType === 'FACING_OPEN_CALL' || betType.includes('FACING_OPEN_CALL')) {
    return config.openPosition
      ? `${position} Call vs ${config.openPosition} Open Range`
      : `${position} Call vs Open Range`;
  }

  if (betType === 'OPEN') return `${position} Open Range`;

  if (betType === 'FACING_3BET_CALL' || betType.includes('FACING_3BET_CALL')) {
    return config.threeBetPosition
      ? `${position} Call vs ${config.threeBetPosition} 3Bet Range`
      : `${position} Call vs 3Bet Range`;
  }

  if (betType === 'FACING_3BET_4BET' || betType.includes('FACING_3BET_4BET')) {
    return config.threeBetPosition
      ? `${position} 4Bet vs ${config.threeBetPosition} 3Bet Range`
      : `${position} 4Bet vs 3Bet Range`;
  }

  if (betType === 'FACING_OPEN_3BET' || betType.includes('FACING_OPEN_3BET')) {
    return config.openPosition
      ? `${position} 3Bet vs ${config.openPosition} Open Range`
      : `${position} 3Bet Range`;
  }

  const action = betType.toLowerCase()
    .replace('facing_', '')
    .replace('_', ' ')
    .replace('3bet', '3Bet')
    .replace('4bet', '4Bet');

  return `${position} ${action} Range`;
};

const ENCOURAGEMENT = {
  low: [
    "Not your final form yet. Run it back.",
    "Rough one. Now you know what to fix.",
    "Missed a few. That's where the edge starts.",
    "Learning spot. Queue it up again.",
  ],
  developing: [
    "Getting there. Just needs tightening.",
    "You see it. Now clean it up.",
    "Solid base. Keep building from here.",
    "Closer than it feels. Stay on it.",
  ],
  strong: [
    "Now we're talking. This is strong play.",
    "Sharp overall. Just refine the edges.",
    "Winning range. Dial in the details.",
    "You're ahead. Now make it consistent.",
  ],
  elite: [
    "Clean. That's high-level play.",
    "Crusher territory. Keep it sharp.",
    "You're not guessing—you know.",
    "Elite. Now make it automatic.",
  ],
};

const getEncouragement = (score) => {
  let pool;
  if (score < 50) pool = ENCOURAGEMENT.low;
  else if (score < 70) pool = ENCOURAGEMENT.developing;
  else if (score < 90) pool = ENCOURAGEMENT.strong;
  else pool = ENCOURAGEMENT.elite;
  return pool[Math.floor(Math.random() * pool.length)];
};

const BlastResultsScreen = () => {
  console.log('BlastResultsScreen mounted');
  const [savingStatus, setSavingStatus] = useState('pending');
  const encouragementRef = useRef(null);
  // Holds the in-flight /api/submit-quiz promise so the nav handlers below
  // can await it before leaving this screen — see savePromiseRef.current
  // usage in handleTryAgain/handleNewQuiz/handleDashboard.
  const savePromiseRef = useRef(null);

  let gameData;
  try {
    gameData = useGame();
  } catch (error) {
    console.error('Failed to load game context:', error);
    return <div>Error: Game context not available</div>;
  }

  const { gameState, startNewGame, quizAnalytics } = gameData;
  const navigate = useNavigate();
  const { user } = useAuth();

  const { correctAnswers, questionCount, gameConfig } = gameState;
  const percentage = Math.round((correctAnswers / questionCount) * 100);

  if (!encouragementRef.current) {
    encouragementRef.current = getEncouragement(percentage);
  }

  // Game type label: Blast - 3 Way or Blast - Heads Up
  const getGameTypeLabel = () => {
    if (gameConfig.format === 'hu') return 'Blast - Heads Up';
    if (gameConfig.format === '3way') return 'Blast - 3 Way';
    return 'Blast';
  };

  const rangeTitle = formatTitle(
    gameConfig.heroPosition,
    gameConfig.betType,
    {
      openPosition: gameConfig.openPosition,
      threeBetPosition: gameConfig.threeBetPosition
    }
  );
  const quizTypeString = rangeTitle;

  useEffect(() => {
    const saveQuizResults = async () => {
      try {
        setSavingStatus('pending');

        const results  = quizAnalytics.results || [];
        // Fall back to currentQuiz if finalization hasn't run yet
        const lastQuiz = results[results.length - 1] || quizAnalytics.currentQuiz;
        const now      = new Date().toISOString();

        const sessionId   = lastQuiz?.id       || `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const startedAt   = lastQuiz?.startTime || now;
        const completedAt = lastQuiz?.endTime   || now;
        const questions   = lastQuiz?.questions || [];

        const sessionPayload = {
          session_id:      sessionId,
          user_id:         user?.uid,
          started_at:      startedAt,
          completed_at:    completedAt,
          status:          'completed',
          quiz_type:       'BLAST',
          game_format:     gameConfig.format    || null,
          stack_depth:     gameConfig.stack     || null,
          hero_position:   gameConfig.heroPosition || gameConfig.position || null,
          villain_position: gameConfig.villainPos  || null,
          scenario_node:   questions[0]?.scenario_node || null,
          action_type:     questions[0]?.action_type   || null,
          training_filter: gameConfig.filterMode       || null,
          bet_type:        null,
          open_position:   null,
          three_bet_position: null,
          total_questions: questionCount,
          total_correct:   correctAnswers,
          score_percentage: percentage,
        };

        const questionsPayload = questions.map((q, idx) => ({
          answered_at:       q.answered_at  || q.timestamp || now,
          question_number:   q.questionNumber || q.question_number || (idx + 1),
          quiz_type:         'BLAST',
          game_format:       q.game_format  || sessionPayload.game_format,
          stack_depth:       q.stack_depth  || sessionPayload.stack_depth,
          hero_position:     q.hero_position || sessionPayload.hero_position,
          villain_position:  q.villain_position || sessionPayload.villain_position,
          scenario_node:     q.scenario_node || null,
          action_type:       q.action_type  || null,
          training_filter:   q.training_filter || null,
          bet_type:          null,
          hand_combo:        q.hand_combo   || '',
          hand_class:        q.hand_class   || '',
          suitedness:        q.suitedness   || 'offsuit',
          hand_category:     q.hand_category || null,
          rank_cluster:      q.rank_cluster  || null,
          correct_action:    q.correct_action  || 'Jam',
          correct_frequency: q.correct_frequency ?? null,
          correct_raise_size: null,
          selected_action:   q.selected_action  || 'Fold',
          selected_frequency: q.selected_frequency ?? null,
          selected_raise_size: null,
          is_correct:        q.isCorrect ?? q.is_correct ?? false,
          response_time_ms:  q.response_time_ms || null,
          error_type:        q.error_type   || null,
        }));

        const payload = { session: sessionPayload, questions: questionsPayload };
        console.log('[blast submit-quiz] sending payload:', JSON.stringify(payload, null, 2));

        const response = await fetchWithRetry('/api/submit-quiz', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Cache-Control': 'no-cache' },
          body: JSON.stringify(payload)
        });

        const responseBody = await response.json().catch(() => ({}));
        console.log('[blast submit-quiz] response:', response.status, responseBody);

        if (response.ok) {
          setSavingStatus('success');
        } else {
          console.warn('[blast submit-quiz] FAILED:', responseBody);
          setSavingStatus('error');
        }
      } catch (error) {
        console.warn('[blast submit-quiz] exception:', error);
        setSavingStatus('error');
      }
    };

    savePromiseRef.current = saveQuizResults();
  }, []);

  // All three nav handlers await the /api/submit-quiz save before leaving
  // this screen. Without this, a fast tap (very plausible right after
  // seeing results) can navigate to /dashboard before the POST's DB commit
  // finishes, so the dashboard's next GET /api/user-stats reads a stale
  // total_quizzes and the empty state incorrectly stays up. The server
  // commits before responding (see server.js /api/submit-quiz), so
  // awaiting the resolved fetch is sufficient — no server change needed.
  const handleTryAgain = async () => {
    await savePromiseRef.current;
    await startNewGame({
      ...gameConfig,
      resetType: 'SAME_CONFIG',
      quizType: 'BLAST'
    });
    navigate('/blast');
  };

  const handleNewQuiz = async () => {
    await savePromiseRef.current;
    await startNewGame({ resetType: 'FULL_RESET' });
    navigate('/blast-config');
  };

  const handleDashboard = async () => {
    await savePromiseRef.current;
    await startNewGame({ resetType: 'FULL_RESET' });
    navigate('/dashboard');
  };

  return (
    <div className="results-screen blast-results-anchor">
      <div className="results-content">

        <div className="results-top">
          <div className="results-image-wrap">
            <img
              src="/svgs/results.svg"
              alt="Quiz Results"
              className="results-icon"
            />
          </div>

          <div className="results-header">
            <p className="results-game-type">{getGameTypeLabel()}</p>
            <h1 className="results-title">Quiz Results</h1>
            <p className="results-subtitle">{quizTypeString}</p>
          </div>

          <div className="results-score">
            <div className="results-percentage">{percentage}<span className="results-pct-sign">%</span></div>
            <p className="results-correct-text">{correctAnswers} of {questionCount} Correct</p>
          </div>

        </div>

      </div>

      {/* Sibling of .results-content, not nested inside it — .results-content
          is the element that actually scrolls (overflow-y: auto), and an
          absolutely positioned descendant of a scrolling container still
          scrolls and clips away with it regardless of what its containing
          block is. Being a sibling anchored to .blast-results-anchor
          (.results-screen, non-scrolling) is what keeps this pinned in
          place, mirroring BlastAnswerScreen's .answer-scroll-gradient
          pattern. */}
      <QuizFooter horizontalPadding={16} bottomFallback={24} rowGap={10} background="#F9F5EF">
        <QuizFooterRow gap={10}>
          <button className="blast-results-btn blast-results-btn--green" onClick={handleTryAgain}>
            Try Again
          </button>
          <button className="blast-results-btn blast-results-btn--blue" onClick={handleNewQuiz}>
            New Quiz
          </button>
        </QuizFooterRow>
        <button className="blast-results-btn blast-results-btn--orange blast-results-btn--full" onClick={handleDashboard}>
          Dashboard
        </button>
      </QuizFooter>
    </div>
  );
};

export default BlastResultsScreen;
